<template>
  <div class="timeline-action" :class="actionClasses(action)">
    <md-card>
      <md-ripple>
        <md-card-header @click.native="expandOrCollapse">
          <div>
            <md-icon class="md-size-2x">{{ actionIcon(action) }}</md-icon>
          </div>
          <div class="action-heading-container">
            <div class="md-title">{{ actionTitle(action) }}</div>
            <div class="md-subhead">{{ actionDescription(action) }}</div>
          </div>
          <div>
            <md-icon>
              {{
                state.expanded ? "expand_less" : "expand_more"
              }}
            </md-icon>
          </div>
        </md-card-header>
      </md-ripple>

      <md-card-content v-if="state.expanded">
        <table>
          <tr v-if="action.type === 'MEDICATION'">
            <td>Dosage</td>
            <td>{{ extraInfos(action).MEDICATION_DOSE }}{{ unitLabel(extraInfos(action).MEDICATION_UNIT_TYPE) }}</td>
          </tr>
          <tr v-if="(action.type === 'MEDICATION' || action.type === 'INFUSION_MEDICATION' || action.type === 'INFUSION_FLUID') && getInfusionBagVolume(action)">
            <td>Bag Volume</td>
            <td>{{ getInfusionBagVolume(action) }}</td>
          </tr>
          <tr v-if="(action.type === 'INFUSION_MEDICATION' || action.type === 'INFUSION_FLUID') && getInfusionRatePrimary(action)">
            <td>Primary Rate</td>
            <td>{{ getInfusionRatePrimary(action) }}</td>
          </tr>
          <tr v-if="(action.type === 'INFUSION_MEDICATION' || action.type === 'INFUSION_FLUID') && getInfusionRateSecondary(action)">
            <td>Secondary Rate</td>
            <td>{{ getInfusionRateSecondary(action) }}</td>
          </tr>
          <tr v-if="(action.type === 'INFUSION_MEDICATION' || action.type === 'INFUSION_FLUID') && getInfusionRateClamp(action)">
            <td>Rate</td>
            <td>{{ getInfusionRateClamp(action) }}</td>
          </tr>
          <tr v-if="(action.type === 'INFUSION_MEDICATION' || action.type === 'INFUSION_FLUID') && getInfusionVTBI(action)">
            <td>VTBI</td>
            <td>{{ getInfusionVTBI(action) }}</td>
          </tr>
          <tr v-if="(action.type === 'INFUSION_MEDICATION' || action.type === 'INFUSION_FLUID') && getInfusionLocation(action)">
            <td>Location</td>
            <td>{{ getInfusionLocation(action) }}</td>
          </tr>
          <tr v-if="action.type === 'OXYGEN' && action.typeID !== 'NONE'">
            <td>Dosage</td>
            <td>{{ extraInfos(action).OXYGEN_DOSE }}L</td>
          </tr>
          <tr v-if="action.type === 'IV_ACTION' && action.typeID !== 'NONE'">
            <td>Options</td>
            <td>{{ ivExtraInfosLabel(action) }}</td>
          </tr>
          <tr v-if="actionGrade(action) !== null">
            <td>Grade</td>
            <td>{{ actionGrade(action) }}</td>
          </tr>
          <tr v-if="actionIsCritical(action) !== null">
            <td>Critical</td>
            <td>{{ actionIsCritical(action) }}</td>
          </tr>
          <tr>
            <td>
              {{ heartRhythmLabel(action.patientSnapshot.vitals.heartRhythm) }}
            </td>
            <td>{{ round(action.patientSnapshot.vitals.heartRate) }}bpm</td>
          </tr>
          <tr>
            <td>BP</td>
            <td>
              {{
                round(action.patientSnapshot.vitals.bloodPressureSystolic)
              }}/{{ round(action.patientSnapshot.vitals.bloodPressureDiastolic) }}
            </td>
          </tr>
          <tr>
            <td>ETCO2</td>
            <td>
              {{ etco2RhythmLabel(action.patientSnapshot.vitals.etco2Rhythm) }}
              {{ round(action.patientSnapshot.vitals.etco2) }}
            </td>
          </tr>
          <tr>
            <td>RR</td>
            <td>{{ round(action.patientSnapshot.vitals.respiratoryRate) }}</td>
          </tr>
          <tr>
            <td>SPO2</td>
            <td>{{ round(action.patientSnapshot.vitals.spo2) }}</td>
          </tr>
          <tr>
            <td>Temp</td>
            <td>{{ round(action.patientSnapshot.vitals.temperature, 1) }}</td>
          </tr>
          <tr>
            <td>Glucose</td>
            <td>{{ round(glucoseLabValue(action).valueFloat) }}</td>
          </tr>
        </table>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import fromPairs from 'lodash/fromPairs';
import {
  categoryLabel,
  categoryVerb,
  actionLabel,
} from '~lib/common/utils/gradingReports';
import { unitLabel } from '~lib/common/utils/labels';

import { infusionBagVolumes } from '~lib/common/config/medication'
import iv from '~lib/common/config/iv'

import getGradingActionFromTimelineAction from './getGradingActionFromTimelineAction';
import actionTypeIcons from './actionTypeIcons';

import etco2Rhythms from './etco2Rhythms';
import heartRhythms from './heartRhythms';

export default {
  name: 'timeline-action',
  props: {
    action: {
      type: Object,
      required: true,
    },
    report: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      state: {
        expanded: false,
      },
    };
  },
  methods: {
    actionIcon(action) {
      return actionTypeIcons[action.type] || 'directions_run';
    },
    actionTitle(action) {
      return categoryLabel(action) + ' ' + categoryVerb(action);
    },
    actionDescription(action) {
      const { report } = this;
      const gradingAction = getGradingActionFromTimelineAction(report, action);
      return gradingAction ? actionLabel(gradingAction) || actionLabel(action) : actionLabel(action);
    },
    actionGrade(action) {
      const { report } = this;
      const gradingAction = getGradingActionFromTimelineAction(report, action);
      return gradingAction ? gradingAction.points : null;
    },
    actionIsCritical(action) {
      const { report } = this;
      const gradingAction = getGradingActionFromTimelineAction(report, action);
      return gradingAction ? gradingAction.isCritical ? 'Yes' : 'No' : null;
    },
    round(value, digits) {
      return value ? value.toFixed(digits) : value;
    },
    glucoseLabValue(action) {
      return action.patientSnapshot.labValues.find(
        (labValue) => labValue.typeID === 'GLUCOSE'
      );
    },
    etco2RhythmLabel(etco2RhythmIntValue) {
      const etco2Rhythm = etco2Rhythms[etco2RhythmIntValue];
      if (etco2Rhythm) {
        return etco2Rhythm.label.en;
      }
      return 'Unknown';
    },
    heartRhythmLabel(heartRhythmIntValue) {
      const heartRhythm = heartRhythms[heartRhythmIntValue];
      if (heartRhythm) {
        return heartRhythm.label.en;
      }
      return 'Unknown';
    },
    actionClasses(action) {
      const { report } = this;

      const gradingAction = getGradingActionFromTimelineAction(report, action);
      if (!gradingAction) {
        return;
      }
      return `${gradingAction.isSuccess ? 'good' : ''} ${
        gradingAction.isFailure ? 'bad' : ''
      } ${gradingAction.isCritical ? 'critical' : ''}`;
    },
    extraInfos(action) {
      if (!action.extraInfos) {
        return {};
      }
      return fromPairs(action.extraInfos.map(extraInfo => [extraInfo.key, extraInfo.value]));
    },
    ivExtraInfosLabel(timelineAction) {
      const { extraInfos } = timelineAction;
      return extraInfos.filter(extraInfo => !(extraInfo.key === 'IV_SALINE_LOCK' && extraInfo.value === 'False')).map(extraInfo => actionLabel({
        type: extraInfo.key,
        typeID: extraInfo.value
      })).join(' ')
    },
    getExtraInfo(timelineAction, infoKey) {
      return timelineAction.extraInfos.find(info => info.key === infoKey);
    },
    getInfusionBagVolume(timelineAction) {
      let value = null;
      const bagVolumeInfo = this.getExtraInfo(timelineAction, 'INFUSION_BAG_VOLUME');
      if (bagVolumeInfo) {
        const bagVolume = infusionBagVolumes.find(volume => volume.key === bagVolumeInfo.value);
        if (bagVolume) {
          value = bagVolume.label.en;
        }
      }
      return value;
    },
    getInfusionRatePrimary(timelineAction) {
      const rateInfo = this.getExtraInfo(timelineAction, 'PRIMARY_ML_PER_HR');
      if (rateInfo) {
        const rate = parseFloat(rateInfo.value);
        return rate.toFixed(1) + ' mL/hr';
      }
    },
    getInfusionRateSecondary(timelineAction) {
      const rateInfo = this.getExtraInfo(timelineAction, 'SECONDARY_ML_PER_HR');
      if (rateInfo) {
        const rate = parseFloat(rateInfo.value);
        return rate.toFixed(1) + ' mL/hr';
      }
    },
    getInfusionRateClamp(timelineAction) {
      const gttsInfo = this.getExtraInfo(timelineAction, 'GTTS_PER_MIN');
      if (gttsInfo) {
        const rate = parseFloat(gttsInfo.value);
        return rate.toFixed(1) + ' gtts/min'
      }
    },
    getInfusionVTBI(timelineAction) {
      const vtbiInfo = this.getExtraInfo(timelineAction, 'VTBI');
      if (vtbiInfo) {
        const vtbi = parseFloat(vtbiInfo.value);
        return vtbi.toFixed(0) + ' mL';
      }
    },
    getInfusionLocation(timelineAction) {
      const locationInfo = this.getExtraInfo(timelineAction, 'LOCATION');
      if (locationInfo) {
        // eslint-disable-next-line no-unused-vars
        const [ sideKey, siteKey, category, portKey ] = locationInfo.value.split(' ');
        const side = iv.sides.find(side => side.key === sideKey);
        const site = iv.sites.find(site => site.key === siteKey);
        const portName = iv.portNames.find(portName => portName.key === portKey);
        let locationStr = `${side ? side.label.en : ''}`;
        locationStr += `/${site ? site.label.en : ''}`
        locationStr += `/${portName ? portName.label.en : ''}`
        return locationStr;
      }
    },
    expandOrCollapse() {
      this.state.expanded = !this.state.expanded;
    },
    unitLabel,
  },
};
</script>

<style scoped>
td:nth-child(1) {
  font-weight: bold;
}

td:nth-child(1)::after {
  content: ":";
}

.timeline-action >>> .md-card-header {
  padding: 9px;
  background-color: #707070;
  color: #fff;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0;
}

.action-heading-container {
  flex: 1;
}

.md-icon {
  color: #fff !important;
  margin-right: 5px;
}

.timeline-action >>> .md-title {
  margin-top: 0 !important;
  font-size: 18px;
}

.timeline-action >>> .md-subhead {
  font-size: 16px;
  opacity: 1;
}

.timeline-action >>> .md-card-content {
  padding-top: 16px;
  font-size: 16px;
}

.timeline-action.good >>> .md-card-header {
  background-color: #62cd63;
}

.timeline-action.bad >>> .md-card-header {
  background-color: #db4437;
}
</style>